import '@availity/yup';
import * as yup from 'yup';

const COMMA_SEPARATED_VALUES_REGEXP =
  /^[a-zA-Z\u00C0-\u017F0-9\s'\-./]+(?:(?:,)[a-zA-Z\u00C0-\u017F0-9\s'\-./]+)*[a-zA-Z\u00C0-\u017F0-9\s'\-./]+$/g;

const formValidation = yup.object().shape({
  name: yup.string().required(),
  customerId: yup.string().required(),
  isRCS: yup.boolean(),
  startDate: yup.date().required(),
  endDate: yup.date().required(), //.min(yup.ref('startDate')),
  priority: yup.number().required().positive().integer(),
  leads: yup.number().required().integer(),
  payout: yup.number().required().min(0),
  percentageRebate: yup.number().required().min(0),
  /*messageTemplateId: yup
    .string()
    .required()
    .when('sendLeadType', {
      is: 'trackingPixel',
      then: () => yup.string().matches(/^(?!.*none).*$/),
    }),*/
  messageTemplateId: yup.string().required(),
  messageSendDelay: yup.number().when('messageTemplateId', {
    is: (value) => value && value !== 'none',
    then: () => yup.number().required().min(0),
  }),
  sendLeadType: yup.string().required(),
  landingPageCustomer: yup.string().when('sendLeadType', {
    is: (value) =>
      (value === 'dataEntry' || value === 'trackingPixel') &&
      !yup.ref('landingPageLead'),
    then: () => yup.string().required(),
  }),
  landingPageLead: yup.string().when('sendLeadType', {
    is: (value) =>
      (value === 'dataEntry' || value === 'trackingPixel') &&
      !yup.ref('landingPageCustomer'),
    then: () => yup.string().required(),
  }),
  trackingMessageTemplateId: yup.string().when('sendLeadType', {
    is: 'trackingPixel',
    then: () => yup.string().required(),
  }),
  webService: yup.string().when('sendLeadType', {
    is: 'webService',
    then: () => yup.string().required(),
  }),
  spreadsheetLeadFieldIds: yup.array().when('sendLeadType', {
    is: 'googleSpreadsheets',
    then: () => yup.array().required().min(3),
  }),
  costPositiveClosure: yup.number().required().min(0),
  validLeads: yup.number().min(0),
  reasons: yup.string().matches(COMMA_SEPARATED_VALUES_REGEXP, {
    excludeEmptyString: true,
  }),
  supplierId: yup.string(),
  percentageRevenueShare: yup.number().when('supplierId', {
    is: (value) => !!value,
    then: () => yup.number().min(0).required(),
  }),
  percentageMarginShare: yup.number().when('supplierId', {
    is: (value) => !!value,
    then: () => yup.number().min(0).required(),
  }),
  sendSchedule: yup
    .object()
    .shape({
      enabled: yup.boolean(),
      matrix: yup.object(),
    })
    .test(
      'non-negative-integers',
      'Schedule values must be integers >= 0',
      function (value) {
        const scheduleSupportedSendLeadTypes = [
          'webService',
          'googleSpreadsheets',
        ];
        const sendLeadType = this.parent?.sendLeadType;
        const isScheduleRelevant =
          scheduleSupportedSendLeadTypes.includes(sendLeadType);

        if (!isScheduleRelevant || !value?.enabled) return true;

        const cells = Object.values(value.matrix || {}).flatMap((row) =>
          Object.values(row || {}),
        );

        if (cells.length === 0) return false;

        return cells.every((v) => {
          if (v === '' || v === null || typeof v === 'undefined') {
            return false;
          }
          const numericValue = Number(v);
          return Number.isInteger(numericValue) && numericValue >= 0;
        });
      },
    ),
  // AI module for the offer. `aiEnabled` absent defaults to off, so no
  // existing offers become invalid: the requirement only applies to whoever
  // enables the switch, which is who decided to expose this offer to an agent.
  aiEnabled: yup.boolean(),
  aiPitch: yup.string().when('aiEnabled', {
    is: true,
    then: () => yup.string().trim().required(),
  }),
  aiQualificationCriteria: yup.string().when('aiEnabled', {
    is: true,
    then: () => yup.string().trim().required(),
  }),
  // `aiClosureAllowed` absent defaults to ON: that's how the engine reads it
  // in closureGate, which accepts `undefined` alongside `true`. So use
  // `!== false`, never `=== true`, else every offer that never touched that
  // flag would escape the requirement precisely while the AI closes those
  // offers on its own.
  aiClosingMessage: yup.string().when(['aiEnabled', 'aiClosureAllowed'], {
    is: (aiEnabled, aiClosureAllowed) =>
      aiEnabled === true && aiClosureAllowed !== false,
    then: () => yup.string().trim().required(),
  }),
  // Required only when the AI cannot close this offer on its own, and that is
  // exact rather than a concession. With a multi-offer proposal taking its
  // sentence from the acquisition order, this text is read only for a
  // proposal on ONE offer — and a single-offer proposal can only be an offer
  // with aiClosureAllowed false: if anything is waiting for confirmation
  // there is at least one such offer, and when the group holds one that one
  // IS it. An offer that may close on its own, alone, closes.
  //
  // Mirror image of aiClosingMessage above: exactly one of the two is
  // required, always.
  aiProposalMessage: yup.string().when(['aiEnabled', 'aiClosureAllowed'], {
    is: (aiEnabled, aiClosureAllowed) =>
      aiEnabled === true && aiClosureAllowed === false,
    then: () => yup.string().trim().required(),
  }),
  aiClosureAllowed: yup.boolean(),
});

export default formValidation;
